import { X, Users, MessageCircle, Layers, Check, Zap } from "lucide-react";
import { Crown, Star, Rocket } from "lucide-react";

const planIcons = {
  free: <Crown className="text-yellow-400" size={28} />,
  pro: <Star className="text-blue-500" size={28} />,
  enterprise: <Rocket className="text-purple-500" size={28} />,
};

const planColors = {
  free: 'from-yellow-400 to-yellow-500',
  pro: 'from-blue-500 to-blue-600',
  enterprise: 'from-purple-500 to-purple-600'
};

// Removed unused FeatureIcon component since we're now handling icons directly in PlanFeature

const PlanFeature = ({ icon: Icon, label, value, planType = 'free' }) => {
  // Define colors based on plan type
  const colors = {
    free: 'yellow',
    pro: 'blue',
    enterprise: 'purple'
  };
  const color = colors[planType] || 'gray';
  
  return (
    <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-bg-50 dark:bg-bg-70 hover:bg-opacity-50 transition-colors">
      <div className="flex items-center space-x-3">
        <div>
          <Icon className={`w-4 h-4 text-${color}-500`} />
        </div>
        <span className="text-sm font-medium text-fg-80">{label}</span>
      </div>
      <span className="text-sm font-semibold text-fg-90">{value}</span>
    </div>
  );
};

const PlanUpgradeSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
    {[1, 2, 3].map((_, idx) => (
      <div
        key={idx}
        className="border border-bd-50 rounded-2xl p-6 flex flex-col h-full animate-pulse bg-bg-60"
      >
        <div className="w-14 h-14 rounded-xl mb-4 bg-bg-40" />
        <div className="h-6 bg-bg-40 rounded w-1/2 mb-4" />
        <div className="h-8 bg-bg-40 rounded w-1/3 mb-4" />
        <div className="space-y-3 my-4 border-t border-bd-50 pt-4">
          <div className="h-4 bg-bg-40 rounded w-3/4" />
          <div className="h-4 bg-bg-40 rounded w-2/3" />
          <div className="h-4 bg-bg-40 rounded w-1/2" />
        </div>
        <div className="mt-auto pt-4">
          <div className="h-10 bg-bg-40 rounded w-full" />
        </div>
      </div>
    ))}
  </div>
);

const PlanUpgradeModal = ({
  isOpen,
  onClose,
  currentPlanId,
  onPlanSelect,
  plans = [],
  employeeName,
  currentPlanName = '',
  isLoading = false,
}) => {
  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 animate-fadeIn overflow-y-auto">
        <div className="bg-white dark:bg-bg-60 rounded-2xl shadow-2xl w-full max-w-5xl border border-bd-50 animate-modalPop my-8">
          <div className="p-8">
            <div className="flex justify-between items-center mb-8">
              <div className="flex flex-col gap-1">
                <div>
                <h2 className="text-2xl font-bold text-fg-80">
                  Upgrade Plan for <span className="text-ac-02">{employeeName}</span>
                </h2>
                <p className="text-fg-60 mt-1">Choose the perfect plan for your needs</p>
              </div>
              </div>
              <button
                onClick={onClose}
                className="text-fg-60 hover:text-fg-50 rounded-full p-2 transition-colors"
                aria-label="Close"
              >
                <X size={24} />
              </button>
            </div>
            
            {isLoading || plans.length === 0 ? (
              <PlanUpgradeSkeleton />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {plans.map((plan) => {
                  const planId = plan._id || plan.id;
                  const isCurrent = planId === currentPlanId || plan.name === currentPlanId;
                  const planName = plan.name?.toLowerCase();
                  const PlanIcon = planIcons[planName]?.type || Crown;
                  
                  return (
                    <div
                      key={planId}
                      className={`relative border rounded-2xl p-6 flex flex-col transition-all duration-300 cursor-pointer group
                        ${isCurrent 
                          ? 'border-ac-02 bg-ac-02/5 scale-[1.02] ring-1 ring-ac-02' 
                          : 'border-bd-50 hover:border-ac-02 hover:shadow-lg'}
                      `}
                      onClick={() => onPlanSelect(plan)}
                    >
                      <div className="absolute -top-3 left-0 right-0 flex justify-between px-4">
                        {/* Current Plan Badge - Left side */}
                        {isCurrent && (
                          <div className="relative">
                            <div className="px-3 py-1 rounded-full text-xs font-semibold bg-ac-02 text-white dark:text-black shadow-md">
                              Current Plan
                            </div>
                          </div>
                        )}
                        
                        {/* MOST POPULAR Badge - Right side */}
                        {planName === 'pro' && (
                          <div className="relative transform rotate-3">
                            <div className="relative">
                              <div className="absolute -inset-1 bg-blue-500/20 rounded-lg blur opacity-75"></div>
                              <div className="relative px-4 py-1.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs font-bold rounded-md shadow-lg">
                                <span className="relative z-10">MOST POPULAR</span>
                                <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-0 h-0 
                                  border-l-4 border-l-transparent
                                  border-t-4 border-t-blue-600
                                  border-r-4 border-r-transparent"></div>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Empty div to maintain space when no current plan */}
                        {!isCurrent && <div></div>}
                      </div>
                      
                      <div className={`relative w-14 h-14 rounded-xl mb-4 flex items-center justify-center ${planColors[planName]} bg-gradient-to-r text-white`}>
                        <PlanIcon size={28} className="text-white" />
                      </div>
                      
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-xl font-bold text-fg-90">
                          {plan.name?.charAt(0).toUpperCase() + plan.name?.slice(1)}
                        </h3>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-fg-90">
                            ${plan.price}
                          </div>
                          <div className="text-xs text-fg-50">per month</div>
                        </div>
                      </div>
                      
                      <div className="space-y-3 my-4 border-t border-bd-50 pt-4">
                        <PlanFeature 
                          icon={Users}
                          label="Employee Limit"
                          value={plan.employeeLimit || 'Unlimited'}
                          planType={planName}
                        />
                        <PlanFeature 
                          icon={MessageCircle}
                          label="Chat Limit"
                          value={plan.chatLimit !== undefined ? `${plan.chatLimit} per month` : 'Unlimited'}
                          planType={planName}
                        />
                        <PlanFeature 
                          icon={Layers}
                          label="Thread Limit"
                          value={plan.threadLimit !== undefined ? plan.threadLimit : 'Unlimited'}
                          planType={planName}
                        />
                      </div>
                      <div className="mt-auto pt-4">
                        <button
                          className={`w-full py-3 px-6 rounded-lg font-medium transition-all flex items-center justify-center space-x-2
                            ${isCurrent
                              ? 'bg-bg-60 text-fg-60 cursor-default border border-bd-50'
                              : `bg-gradient-to-r ${planColors[planName] || 'from-gray-500 to-gray-600'} text-white hover:opacity-90 hover:shadow-lg transform hover:-translate-y-0.5`
                            } ${isLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
                          disabled={isCurrent || isLoading}
                        >
                          {isLoading ? (
                            <>
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                              <span>Processing...</span>
                            </>
                          ) : isCurrent ? (
                            <>
                              <Check size={18} className="mr-2" />
                              Current Plan
                            </>
                          ) : (
                            <>
                              <Zap size={16} className="mr-2" />
                              Select Plan
                            </>
                          )}
                        </button>
                        

                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            <div className="mt-8 text-center text-sm text-fg-60">
              <p>Need a custom plan for your organization?</p>
              <a href="#contact" className="text-ac-02 hover:underline">Contact our sales team</a>
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        .animate-fadeIn { animation: fadeIn 0.2s ease; }
        .animate-modalPop { animation: modalPop 0.25s cubic-bezier(.4,2,.6,1) }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes modalPop { 0% { transform: scale(0.85); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
      `}</style>
    </>
  );
};

export default PlanUpgradeModal;
